/* eslint-disable import/no-unresolved */
import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { saveAs } from 'file-saver';

import { COMPARE_MDRM, DOWNLOAD_COMPARE_MDRM_RESULT, GET_ITEM_CODES } from 'Apollo/Queries/CrossReportValidation';
import ReportItemCode from '../components/ReportItemCode';

const ReportItemCodeContainer = ({
    selectedPrimaryReport, selectedCrossReportsArray,
}) => {
    const [primaryReport, setPrimaryReport] = useState(null);
    const [crossReportArray, setCrossReportArray] = useState([]);
    const [selectedCrossReportArray, setSelectedCrossReportArray] = useState([]);
    const [updatedRelatedReports, setUpdatedRelatedReports] = useState([]);
    const [itemCodes, setItemCodes] = useState([]);
    const [itemCodeArray, setItemCodeArray] = useState([]);
    const [selection, setSelection] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [filterString, setFilterString] = useState('');
    const [selectedItemCodes, setSelectedItemCodes] = useState([]);
    const [tabDefinitions, setTabDefinitions] = useState([{
        itemCode: 'ITEM CODE',
    }]);
    const [activeTab, setActiveTab] = useState(null);
    const [compareMdrmData, setCompareMdrmData] = useState([]);
    const [selectedItemCodeData, setSelectedItemCodeData] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [selectedFileFormat, setSelectedFileFormat] = useState('');
    const [pageSizeOptions, setPageSizeOptions] = useState([5, 10, 20, 25, 50, 100]);
    const [pageSize, setPageSize] = useState(100);

    const [getItemCodes, { data: itemCodeData, loading: itemCodeLoading, error: itemCodeError }] = useLazyQuery(GET_ITEM_CODES, {
        fetchPolicy: 'no-cache',
    });

    const [compareMdrm, { data, loading, error }] = useLazyQuery(COMPARE_MDRM, {
        variables: {
            primaryReport: { reportId: primaryReport?.primaryReport?.id, period: primaryReport?.primaryReportPeriod?.period },
            relatedReports: updatedRelatedReports,
            itemCodes: selectedItemCodes,
        },
        fetchPolicy: 'no-cache',
    });

    const [downloadMdrmResult, { data: downloadMdrmResultData, loading: downloadMdrmResultLoading, error: downloadMdrmResultError }] = useLazyQuery(DOWNLOAD_COMPARE_MDRM_RESULT, {
        fetchPolicy: 'no-cache',
    });

    const getSizeHeaderProps = () => ({
        className: 'demo-table-small-column',
    });

    const getSizeProps = () => ({
        className: 'demo-table-small-column',
    });
    const itemCodeColumns = [
        {
            Header: 'Item Code',
            accessor: 'itemCode',
            width: 100,
        },
        {
            Header: 'Report Count',
            accessor: 'reportCount',
            getHeaderProps: getSizeHeaderProps,
            getProps: getSizeProps,
            width: 120,
        },
    ];

    const itemCodeMdrmColumns = [
        {
            Header: 'Reporting Form',
            accessor: 'reportName',
            width: 200,
        },
        {
            Header: 'MDRM',
            accessor: 'mdrm',
            width: 150,
        },
        {
            Header: 'Schedule',
            accessor: 'schedule',
            width: 150,
        },
        {
            Header: 'Line Item',
            accessor: 'lineItem',
            width: 120,
        },
        {
            Header: 'Value',
            accessor: 'value',
            width: 80,
        },
    ];

    const onChangeTab = (e, v) => {
        const itemDetails = compareMdrmData.filter(mdrm => mdrm.itemCode === v)[0];
        setActiveTab(v);
        setSelectedItemCodeData(itemDetails);
    };

    const onExportToCSV = async () => {
        setIsDownloading(true);
        downloadMdrmResult({
            variables: {
                primaryReport: { reportId: selectedPrimaryReport?.primaryReport?.id, period: selectedPrimaryReport?.primaryReportPeriod?.period },
                relatedReports: updatedRelatedReports,
                itemCodes: selectedItemCodes,
                format: selectedFileFormat,
            },
        });
    };

    const onSelectFileFormat = format => {
        setSelectedFileFormat(format);
    };

    const onChangeCheckbox = (e, value) => {
        setCrossReportArray(prevState => {
            const prevData = [...prevState];
            const updatedData = prevData.map(item => (
                (item.id === value) ? { ...item, checked: e.target.checked } : { ...item }
            ));
            return updatedData;
        });
    };

    useEffect(() => {
        setItemCodes([]);
        setPrimaryReport(selectedPrimaryReport);
        setTabDefinitions([{
            itemCode: 'ITEM CODE',
        }]);
        setActiveTab('ITEM CODE');
        setSelectedItemCodeData(null);
    }, [selectedPrimaryReport]);

    useEffect(() => {
        setItemCodes([]);
        const crossReportIds = selectedCrossReportsArray.map(scr => ({
            ...scr,
            checked: true,
        }));
        setCrossReportArray(crossReportIds);
    }, [selectedCrossReportsArray]);

    useEffect(() => {
        if (crossReportArray.length > 0) {
            const selectedReports = crossReportArray.filter(scr => scr.checked).map(cr => cr.id);
            setSelectedCrossReportArray(selectedReports);
            const relatedReports = crossReportArray.filter(scr => scr.checked).map(scr => {
                if (scr.quarters) {
                    return scr.quarters.map(q => ({
                        reportId: scr.id,
                        period: q.period,
                    }))[0];
                }
                return {
                    reportId: scr.id,
                    period: null,
                };
            });
            setUpdatedRelatedReports(relatedReports);
        }
    }, [crossReportArray]);

    useEffect(() => {
        if (selectedCrossReportArray.length > 0) {
            getItemCodes({
                variables: {
                    primaryReportId: primaryReport?.primaryReport?.id,
                    relatedReportIds: selectedCrossReportArray,
                },
            });
        } else {
            setItemCodes([]);
            setItemCodeArray([]);
        }
    }, [selectedCrossReportArray]);

    useEffect(() => {
        setSelectAll(false);
        setSelection([]);
        setSelectedItemCodeData(null);
        setSelectedItemCodes([]);
        setTabDefinitions([{
            itemCode: 'ITEM CODE',
        }]);
        setActiveTab('ITEM CODE');
        if (itemCodeData && itemCodeData.DQgetItemCodes) {
            const itemData = itemCodeData.DQgetItemCodes;
            const itemCodeUpdated = [];
            let j = 1;
            for (let i = 0; i < itemData.length; i += 1) {
                const itemCode = {
                    id: j,
                    ...itemData[i],
                };
                itemCodeUpdated.push(itemCode);
                j += 1;
            }
            setItemCodes(itemCodeUpdated);
        }
    }, [itemCodeData]);

    useEffect(() => {
        if (itemCodes.length > 0) {
            setItemCodeArray(itemCodes);
            setPageSizeOptions(prevState => {
                const prevData = [...prevState];
                if (!prevData.includes(itemCodes.length)) {
                    prevData.push(itemCodes.length);
                }
                return prevData;
            });
            setPageSize(itemCodes.length);
        }
    }, [itemCodes]);

    useEffect(() => {
        const searchString = filterString.trim().toLowerCase();
        if (searchString.length > 0) {
            setItemCodeArray(itemCodes.filter(e => String(e.itemCode).toLowerCase().match(searchString)));
        } else {
            setItemCodeArray(itemCodes);
        }
    }, [filterString]);

    useEffect(() => {
        if (selection.length > 0) {
            const codesArray = [];
            const tabs = [];
            for (let i = 0; i < selection.length; i += 1) {
                const code = itemCodes.filter(ic => ic.id === selection[i])[0];
                codesArray.push(code.itemCode);
                tabs.push(code);
            }
            setSelectedItemCodes(codesArray);
            setTabDefinitions(tabs);
            setActiveTab(tabs[tabs.length - 1].itemCode);
            compareMdrm();
        } else {
            setSelectedItemCodes([]);
            setSelectedItemCodeData(null);
            setTabDefinitions([{
                itemCode: 'ITEM CODE',
            }]);
            setActiveTab('ITEM CODE');
        }
    }, [selection]);

    useEffect(() => {
        if (activeTab && data?.DQcompareMdrm.length > 0) {
            const itemDetails = data.DQcompareMdrm.filter(mdrm => mdrm.itemCode === activeTab)[0];
            setCompareMdrmData(data.DQcompareMdrm);
            setSelectedItemCodeData(itemDetails);
        }
    }, [activeTab, data]);

    useEffect(async () => {
        if (downloadMdrmResultData && downloadMdrmResultData.DQdownloadResult) {
            const name = downloadMdrmResultData.DQdownloadResult.fileName;
            const url = downloadMdrmResultData.DQdownloadResult.fileUrl;
            saveAs(url, `${name}`);
            setIsDownloading(false);
        }
    }, [downloadMdrmResultData]);

    useEffect(() => {
        if (downloadMdrmResultError) {
            setIsDownloading(false);
        }
    }, [downloadMdrmResultError]);

    useEffect(() => {
        if (selectedFileFormat !== '') {
            onExportToCSV();
        }
    }, [selectedFileFormat]);

    return (
        <ReportItemCode
            selectedPrimaryReport={primaryReport}
            selectedCrossReportsArray={crossReportArray}
            itemCodeArray={itemCodeArray}
            filterString={filterString}
            setFilterString={setFilterString}
            itemCodeColumns={itemCodeColumns}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            selection={selection}
            setSelection={setSelection}
            activeTab={activeTab}
            onChangeTab={onChangeTab}
            tabDefinitions={tabDefinitions}
            selectedItemCodeData={selectedItemCodeData}
            itemCodeMdrmColumns={itemCodeMdrmColumns}
            itemCodeLoading={loading}
            downloadMdrmResultLoading={isDownloading}
            onSelectFileFormat={onSelectFileFormat}
            onChangeCheckbox={onChangeCheckbox}
            pageSizeOptions={pageSizeOptions}
            pageSize={pageSize}
        />
    );
};

export default ReportItemCodeContainer;
